import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, CheckCircle, Warning } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

export default function ResetPasswordPage({ token, onComplete }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      toast.success(res.data.message);
      setDone(true);
    } catch (err) {
      const msg = err.response?.data?.detail || "Reset failed. The link may have expired.";
      setError(msg);
      toast.error(msg);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#0A0A0A" }}>
      <div className="w-full max-w-sm mx-4">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/skyeai_head.png" alt="Skye AI" className="w-14 h-14 mx-auto mb-4 rounded-2xl" />
          <h1 className="text-xl font-bold text-white">
            {done ? "Password Reset" : "Set New Password"}
          </h1>
          <p className="text-xs text-[#71717A] mt-1">Skye AI Command Center</p>
        </div>

        {done ? (
          <div className="text-center space-y-4">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#10b981]/10 flex items-center justify-center">
              <CheckCircle size={28} weight="fill" className="text-[#10b981]" />
            </div>
            <p className="text-sm text-[#A1A1AA]">
              Your password has been reset successfully.
            </p>
            <button
              onClick={onComplete}
              data-testid="go-to-login"
              className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all"
              style={{ background: "linear-gradient(135deg, #9D32F3, #3B40D4)" }}
            >
              Go to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-[10px] font-bold text-[#71717A] uppercase tracking-wider block mb-1.5">
                New Password
              </label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm bg-white/[0.04] border border-white/[0.08] text-white placeholder-[#52525B] outline-none focus:border-[#9D32F3]/40 transition-all"
                  data-testid="reset-password-input"
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold text-[#71717A] uppercase tracking-wider block mb-1.5">
                Confirm Password
              </label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter password"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm bg-white/[0.04] border border-white/[0.08] text-white placeholder-[#52525B] outline-none focus:border-[#9D32F3]/40 transition-all"
                  data-testid="reset-confirm-input"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#ef4444]/10 border border-[#ef4444]/20" data-testid="reset-error">
                <Warning size={14} className="text-[#ef4444] flex-shrink-0" />
                <p className="text-xs text-[#ef4444]">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #9D32F3, #3B40D4)" }}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>

            <button
              type="button"
              onClick={onComplete}
              className="w-full py-2 text-xs text-[#71717A] hover:text-white transition-all"
            >
              Back to Login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
